import React, { FC, ReactNode } from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';

export type MedalTier = 'bronze' | 'silver' | 'gold' | 'goat';

interface MedalProps {
  /** Ignored when `stops` is supplied. */
  tier: MedalTier;
  /** Icon rendered inside the ring; inherits the tier color via `color`. */
  children: ReactNode;
  size?: number;
  /** Show the mono tier tag chip below the ring. */
  showTag?: boolean;
  /**
   * Explicit ring gradient stops, overriding the tier palette — this is what
   * lets purchased avatar frames reuse the medal ring rather than duplicating it.
   */
  stops?: string[];
  /** Ring thickness. Frames sit tighter than achievement medals. */
  ringWidth?: number;
  /** Inner disc background; frames wrap an avatar and need it transparent. */
  innerBackground?: string;
}

/**
 * Achievement medal ring (guide `.medal`): conic-gradient ring per tier with a
 * solid inner disc and an optional mono tier tag.
 */
const Medal: FC<MedalProps> = ({
  tier,
  children,
  size = 58,
  showTag = true,
  stops: stopsOverride,
  ringWidth = 3.5,
  innerBackground,
}) => {
  const theme = useTheme();
  const stops = stopsOverride ?? theme.tokens.medals[tier];
  const iconColor =
    tier === 'goat' ? theme.tokens.colors.primary : stops[stops.length - 1];
  const ring =
    stops.length >= 3
      ? `conic-gradient(from 200deg, ${stops[0]}, ${stops[1]}, ${stops[2]}, ${stops[0]})`
      : `conic-gradient(from 200deg, ${stops[0]}, ${stops[1]}, ${stops[0]})`;

  return (
    <Box
      sx={{
        position: 'relative',
        width: size,
        height: size,
        borderRadius: '50%',
        padding: `${ringWidth}px`,
        flex: 'none',
        background: ring,
      }}
    >
      <Box
        sx={{
          width: '100%',
          height: '100%',
          borderRadius: '50%',
          backgroundColor: innerBackground ?? theme.tokens.colors.backgroundCard,
          border: innerBackground === 'transparent' ? 'none' : `1px solid ${theme.tokens.colors.glassLine}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: iconColor,
        }}
      >
        {children}
      </Box>
      {showTag && (
        <Box
          component="span"
          sx={{
            position: 'absolute',
            left: '50%',
            bottom: -9,
            transform: 'translateX(-50%)',
            fontFamily: theme.tokens.typography.monoFontFamily,
            fontSize: '8px',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            padding: '2px 8px',
            borderRadius: 'var(--sh-full)',
            backgroundColor: theme.tokens.colors.backgroundCardHover,
            border: `1px solid ${theme.tokens.colors.glassLine}`,
            color: theme.tokens.colors.text2,
            whiteSpace: 'nowrap',
          }}
        >
          {tier}
        </Box>
      )}
    </Box>
  );
};

export default Medal;
